import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Send, MessageSquare, Plus, UserPlus, Hand } from 'lucide-react';
import SLABadge from '@/components/SLABadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Sentinel for the unassign option in the Select control. Shadcn/Radix Select can't
// hold an empty-string value, so we wire null ↔ '__unassigned' at the boundary.
const UNASSIGNED_VALUE = '__unassigned';

const AdminTicketsTab = ({ tickets, users, onRefresh, permissions, currentUserEmail }) => {
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [hideClosedTickets, setHideClosedTickets] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [staffRoster, setStaffRoster] = useState([]);
  const [newTicket, setNewTicket] = useState({
    user_id: '',
    title: '',
    description: '',
    priority: 'medium',
    category: 'general'
  });

  // Permission booleans — fall back to admin-like behaviour if no perms prop was
  // passed (keeps backward compat for any older mounts of this component).
  const isAdmin = permissions?.isAdmin ?? true;
  const isTechnician = permissions?.isTechnician ?? false;
  const me = (currentUserEmail || '').toLowerCase();

  // Fetch staff roster for the assign dropdown — admin + technician only.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/admin/staff`, { withCredentials: true });
        if (!cancelled) setStaffRoster(res.data || []);
      } catch {
        if (!cancelled) setStaffRoster([]);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Assignment filter — 'all' | 'mine' | 'unassigned'. Defaults to 'mine' for
  // technicians (who land on this tab to triage *their* queue) and 'all' for
  // admins (who oversee everything).
  const [assignFilter, setAssignFilter] = useState(isTechnician ? 'mine' : 'all');

  // Apply filters: hide-closed first, then assignment scope.
  let filteredTickets = hideClosedTickets
    ? tickets.filter(t => t.status !== 'closed' && t.status !== 'resolved')
    : tickets;
  if (assignFilter === 'mine' && me) {
    filteredTickets = filteredTickets.filter(
      (t) => (t.assigned_to || '').toLowerCase() === me
    );
  } else if (assignFilter === 'unassigned') {
    filteredTickets = filteredTickets.filter((t) => !t.assigned_to);
  }

  // Counters for the filter chip badges so staff see "how many are mine?"
  const baseTicketsForCounts = hideClosedTickets
    ? tickets.filter(t => t.status !== 'closed' && t.status !== 'resolved')
    : tickets;
  const counts = {
    all: baseTicketsForCounts.length,
    mine: me ? baseTicketsForCounts.filter((t) => (t.assigned_to || '').toLowerCase() === me).length : 0,
    unassigned: baseTicketsForCounts.filter((t) => !t.assigned_to).length,
  };

  const resetNewTicket = () => {
    setNewTicket({
      user_id: '',
      title: '',
      description: '',
      priority: 'medium',
      category: 'general'
    });
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    
    if (!newTicket.user_id) {
      toast.error('Please select a client');
      return;
    }

    setCreating(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/tickets`,
        newTicket,
        { withCredentials: true }
      );
      
      toast.success('Ticket created successfully');
      setShowCreateDialog(false);
      resetNewTicket();
      onRefresh();
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error('Failed to create ticket');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateTicket = async (ticketId, updateData) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/tickets/${ticketId}`,
        updateData,
        { withCredentials: true }
      );
      
      toast.success('Ticket updated successfully');
      onRefresh();
    } catch (error) {
      console.error('Error updating ticket:', error);
      toast.error('Failed to update ticket');
    }
  };

  const handleSendReply = async (ticketId) => {
    if (!replyText.trim()) {
      toast.error('Please enter a response');
      return;
    }

    setSending(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/tickets/${ticketId}`,
        { admin_response: replyText, status: 'in_progress' },
        { withCredentials: true }
      );
      
      toast.success('Response sent successfully');
      setReplyText('');
      setReplyingTo(null);
      onRefresh();
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send response');
    } finally {
      setSending(false);
    }
  };

  // Admin-only: assign or unassign a ticket. `assignee` is an email or null.
  const handleAssign = async (ticketId, assignee) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/tickets/${ticketId}/assign`,
        { assigned_to: assignee },
        { withCredentials: true }
      );
      toast.success(assignee ? `Assigned to ${assignee}` : 'Ticket unassigned');
      onRefresh();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to assign ticket';
      toast.error(typeof detail === 'string' ? detail : 'Failed to assign ticket');
    }
  };

  // Technician self-claim of an unassigned, non-closed ticket. Admins can also call
  // this — but in the UI we hide the button for admins (they have the full picker).
  const handleClaim = async (ticketId) => {
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/tickets/${ticketId}/claim`,
        {},
        { withCredentials: true }
      );
      toast.success(res.data?.message || 'Ticket claimed');
      onRefresh();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to claim ticket';
      toast.error(typeof detail === 'string' ? detail : 'Failed to claim ticket');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'bg-destructive/20 text-destructive';
      case 'high':
        return 'bg-orange-500/20 text-orange-500';
      case 'medium':
        return 'bg-yellow-500/20 text-yellow-500';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'resolved':
      case 'closed':
        return 'bg-primary text-primary-foreground';
      case 'in_progress':
        return 'bg-blue-500/20 text-blue-500';
      default:
        return '';
    }
  };

  // Get user name by ID
  const getUserName = (userId) => {
    const user = users?.find(u => u.user_id === userId);
    return user ? user.name : userId;
  };

  return (
    <div className="space-y-4" data-testid="admin-tickets-tab">
      {/* Header with Filter and Create Button */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-card border border-border rounded-lg">
        <div className="flex flex-wrap items-center gap-3">
          <Switch
            id="hide-closed"
            checked={hideClosedTickets}
            onCheckedChange={setHideClosedTickets}
          />
          <Label htmlFor="hide-closed" className="text-sm cursor-pointer">
            Hide closed & resolved tickets
          </Label>
          <span className="text-sm text-muted-foreground">
            ({filteredTickets.length} of {tickets.length})
          </span>

          {/* My-tickets filter chips — visible to staff. Defaults to 'mine' for techs. */}
          <div className="flex items-center gap-1 ml-1 sm:ml-3" data-testid="assign-filter-chips">
            {[
              { key: 'all',        label: 'All',        testid: 'filter-all' },
              { key: 'mine',       label: 'Mine',       testid: 'filter-mine' },
              { key: 'unassigned', label: 'Unassigned', testid: 'filter-unassigned' },
            ].map((chip) => {
              const active = assignFilter === chip.key;
              const disabled = chip.key === 'mine' && !me;
              return (
                <button
                  type="button"
                  key={chip.key}
                  data-testid={chip.testid}
                  disabled={disabled}
                  onClick={() => setAssignFilter(chip.key)}
                  className={
                    'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-widest transition-colors ' +
                    (active
                      ? 'bg-primary text-primary-foreground'
                      : 'border border-border bg-card text-muted-foreground hover:text-foreground hover:border-primary/40') +
                    (disabled ? ' opacity-40 cursor-not-allowed' : '')
                  }
                >
                  {chip.label}
                  <span className={
                    'rounded-sm px-1 text-[10px] tabular-nums ' +
                    (active ? 'bg-primary-foreground/20' : 'bg-muted/50')
                  }>
                    {counts[chip.key]}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        
        <Dialog open={showCreateDialog} onOpenChange={(open) => {
          setShowCreateDialog(open);
          if (!open) resetNewTicket();
        }}>
          <DialogTrigger asChild>
            <Button className="font-bold uppercase">
              <Plus className="w-4 h-4 mr-2" />
              Create Ticket
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold uppercase">Create Ticket for Client</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTicket} className="space-y-4">
              <div>
                <Label>Client *</Label>
                <Select 
                  value={newTicket.user_id} 
                  onValueChange={(value) => setNewTicket({ ...newTicket, user_id: value })}
                >
                  <SelectTrigger className="bg-input">
                    <SelectValue placeholder="Select a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {users?.filter(u => u.role === 'client').map(user => (
                      <SelectItem key={user.user_id} value={user.user_id}>
                        {user.name} ({user.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Title *</Label>
                <Input
                  value={newTicket.title}
                  onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
                  required
                  placeholder="Ticket title"
                  className="bg-input"
                />
              </div>
              
              <div>
                <Label>Description *</Label>
                <Textarea
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                  required
                  rows={4}
                  placeholder="Describe the issue or request..."
                  className="bg-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Priority</Label>
                  <Select 
                    value={newTicket.priority} 
                    onValueChange={(value) => setNewTicket({ ...newTicket, priority: value })}
                  >
                    <SelectTrigger className="bg-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Category</Label>
                  <Select 
                    value={newTicket.category} 
                    onValueChange={(value) => setNewTicket({ ...newTicket, category: value })}
                  >
                    <SelectTrigger className="bg-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="technical">Technical</SelectItem>
                      <SelectItem value="billing">Billing</SelectItem>
                      <SelectItem value="network">Network</SelectItem>
                      <SelectItem value="security">Security</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <Button type="submit" className="w-full font-bold uppercase" disabled={creating}>
                {creating ? 'Creating...' : 'Create Ticket'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {filteredTickets.map((ticket) => (
        <Card key={ticket.ticket_id} className="p-6 border-border">
          <div className="space-y-4">
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h3 className="font-bold text-lg">{ticket.title}</h3>
                <Badge className={getPriorityColor(ticket.priority)}>
                  {ticket.priority.toUpperCase()}
                </Badge>
                <Badge className={getStatusColor(ticket.status)}>
                  {ticket.status.replace('_', ' ').toUpperCase()}
                </Badge>
                <SLABadge slaState={ticket.sla_state} />
              </div>
              <p className="text-sm text-muted-foreground mb-2">{ticket.description}</p>
              <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                <span className="font-mono">{ticket.ticket_id}</span>
                <span>Category: {ticket.category}</span>
                <span>Client: {getUserName(ticket.user_id)}</span>
                {ticket.attachments?.length > 0 && (
                  <span>{ticket.attachments.length} attachment(s)</span>
                )}
              </div>
              {ticket.created_by_email && (
                <p className="text-xs text-muted-foreground/70 mt-1 italic">
                  Created by <span className="font-mono">{ticket.created_by_email}</span>
                  {ticket.created_by_role && ticket.created_by_role !== 'admin' && (
                    <span className="ml-1 text-[10px] uppercase tracking-widest">({ticket.created_by_role})</span>
                  )}
                </p>
              )}
              {ticket.assigned_to && (
                <p className="text-xs text-muted-foreground/70 italic">
                  Assigned to <span className="font-mono">{ticket.assigned_to}</span>
                </p>
              )}
            </div>
            
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Status:</span>
                <Select
                  value={ticket.status}
                  onValueChange={(value) => handleUpdateTicket(ticket.ticket_id, { status: value })}
                >
                  <SelectTrigger className="w-36 bg-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Admin assign picker — full staff roster, can also unassign. */}
              {isAdmin && (
                <div className="flex items-center gap-2" data-testid={`admin-assign-${ticket.ticket_id}`}>
                  <UserPlus className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Assign:</span>
                  <Select
                    value={ticket.assigned_to || UNASSIGNED_VALUE}
                    onValueChange={(value) =>
                      handleAssign(
                        ticket.ticket_id,
                        value === UNASSIGNED_VALUE ? null : value,
                      )
                    }
                  >
                    <SelectTrigger className="w-56 bg-input" data-testid={`assign-trigger-${ticket.ticket_id}`}>
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={UNASSIGNED_VALUE}>— Unassigned —</SelectItem>
                      {staffRoster.map((s) => (
                        <SelectItem key={s.user_id} value={s.email}>
                          {s.name || s.email} <span className="text-[10px] uppercase text-muted-foreground ml-1">({s.role})</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Technician self-claim — only on unassigned, non-closed tickets. */}
              {!isAdmin && isTechnician && !ticket.assigned_to && ticket.status !== 'closed' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleClaim(ticket.ticket_id)}
                  data-testid={`claim-btn-${ticket.ticket_id}`}
                  className="border-primary/40"
                >
                  <Hand className="w-4 h-4 mr-2" />
                  Claim
                </Button>
              )}

              {/* Technician — show "Mine" pill when already assigned to me. */}
              {!isAdmin && isTechnician && ticket.assigned_to && me && ticket.assigned_to.toLowerCase() === me && (
                <Badge variant="outline" className="border-primary/40 text-primary text-[10px] uppercase tracking-widest" data-testid={`mine-badge-${ticket.ticket_id}`}>
                  Assigned to you
                </Badge>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={() => setReplyingTo(replyingTo === ticket.ticket_id ? null : ticket.ticket_id)}
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                {replyingTo === ticket.ticket_id ? 'Cancel' : 'Reply'}
              </Button>
            </div>

            {/* Reply Form */}
            {replyingTo === ticket.ticket_id && (
              <div className="space-y-3 p-4 bg-muted/20 rounded-lg border border-border">
                <Textarea
                  placeholder="Type your response to the client..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  rows={3}
                  className="bg-input"
                />
                <Button 
                  onClick={() => handleSendReply(ticket.ticket_id)}
                  disabled={sending}
                  className="font-bold"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sending ? 'Sending...' : 'Send Response'}
                </Button>
              </div>
            )}

            {/* Existing Admin Response */}
            {ticket.admin_response && (
              <div className="bg-muted/30 p-4 rounded-lg border-l-4 border-primary">
                <p className="text-xs text-muted-foreground uppercase mb-1">Admin Response</p>
                <p className="text-sm">{ticket.admin_response}</p>
              </div>
            )}
          </div>
        </Card>
      ))}

      {filteredTickets.length === 0 && tickets.length > 0 && (
        <Card className="p-12 text-center border-border">
          <p className="text-muted-foreground">All tickets are closed or resolved.</p>
          <Button 
            variant="link" 
            onClick={() => setHideClosedTickets(false)}
            className="mt-2"
          >
            Show all tickets
          </Button>
        </Card>
      )}

      {tickets.length === 0 && (
        <Card className="p-12 text-center border-border">
          <p className="text-muted-foreground">No tickets found.</p>
        </Card>
      )}
    </div>
  );
};

export default AdminTicketsTab;
